import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Disc3, RefreshCw, Key, KeyRound, Battery, Building2 } from 'lucide-react';
import { COMPANY } from '../constants';
import { useLanguage } from '../i18n/LanguageContext';
import { Button } from './ui/button';

const iconMap = {
  Disc3: Disc3,
  RefreshCw: RefreshCw,
  Key: Key,
  KeyRound: KeyRound,
  Battery: Battery,
  Building2: Building2,
};

const ServiceCard = ({ service, serviceLink, t }) => {
  const IconComponent = iconMap[service.icon];

  return (
    <div
      className="group relative bg-white border border-gray-200 rounded-xl overflow-hidden
        hover:border-[#41DB23] hover:shadow-lg
        transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
      data-testid={`service-card-${service.id}`}
    >
      {/* Image */}
      {service.image && (
        <div className="relative h-40 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
        </div>
      )}

      <div className="p-5 sm:p-6 flex flex-col flex-grow">
        {/* Icon */}
        <div className="w-12 h-12 bg-[#41DB23]/10 border border-[#41DB23]/30 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#41DB23]/20 transition-colors">
          {IconComponent && <IconComponent className="w-6 h-6 text-[#41DB23]" />}
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-[#41DB23] transition-colors">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-grow">
          {service.shortDesc}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-3 mt-auto">
          <a href={COMPANY.phoneLink} className="flex-1">
            <Button
              className="w-full bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold text-sm px-4 py-2.5 rounded-md flex items-center justify-center gap-2 shadow-sm"
              data-testid={`service-call-${service.id}`}
            >
              <Phone className="w-4 h-4" />
              {t.servicesSection.callBtn}
            </Button>
          </a>
          <Link to={serviceLink} className="flex-1">
            <Button
              variant="outline"
              className="w-full border border-[#41DB23] text-[#41DB23] hover:bg-[#41DB23]/10 font-medium text-sm px-4 py-2.5 rounded-md flex items-center justify-center gap-2"
              data-testid={`service-more-${service.id}`}
            >
              {t.servicesSection.moreBtn}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export const Services = () => {
  const { t, services, serviceLink: getServiceLink } = useLanguage();

  return (
    <section
      id="services"
      className="py-16 sm:py-24 lg:py-32 bg-gray-50"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-bold text-[#41DB23] mb-4 block">
            {t.servicesSection.badge}
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-gray-900 mb-4">
            {t.servicesSection.title}
          </h2>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            {t.servicesSection.subtitle}<br />
            {t.servicesSection.subtitleLine2}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              serviceLink={getServiceLink(service)}
              t={t}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className="text-gray-600 mb-4">
            {t.servicesSection.bottomCta}
          </p>
          <a href={COMPANY.phoneLink} data-testid="services-bottom-cta">
            <Button
              size="lg"
              className="bg-[#41DB23] text-white hover:bg-[#38C41E] font-bold uppercase tracking-wide px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-md inline-flex items-center gap-3 shadow-lg"
            >
              <Phone className="w-5 h-5 sm:w-6 sm:h-6" />
              {COMPANY.phone}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
